import React, { useCallback, useEffect, useState } from 'react';
import { MdOutlineModeEditOutline } from 'react-icons/md';
import { ActionIcon, Button, Input, Modal, Textarea, Tooltip } from 'rizzui';
import { toast } from 'sonner';
import DeletePopover from '../../../components/buttons/DeletePopover';
import CustomSelect from '../../../components/forms/CustomSelect';
import { usePrivilege } from '../../../components/priviledge/PriviledgeProvider';
import CustomTable from '../../../components/table/CustomTable';
import useProfessionalAreas from '../../../hooks/api-calls/useProfessionalAreas';
import useFetch from '../../../hooks/useFetch';
import useFilters from '../../../hooks/useFilters';
import useHandleErrors from '../../../hooks/useHandleErrors';
import { TrainingActionGroupService } from '../../../services/training-action-group/trainingActionGroupService';

const columnOrder = ['id', 'code', 'name', 'professionalArea', 'description'];

const TrainingActionGroupModalForm: React.FC<{
    data: {
        id?: string;
        code?: string;
        name?: string;
        description?: string | null;
        professionalAreaId?: string;
    } | null;
    isOpen: boolean;
    onClose: () => void;
    refetch: () => void;
}> = ({ data, isOpen, onClose, refetch }) => {
    const [professionalAreaId, setProfessionalAreaId] = useState('');
    const [code, setCode] = useState('');
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [loading, setLoading] = useState(false);
    const { handleErrors } = useHandleErrors();
    const { fetchProfessionalAreas, getProfessionalAreasList } = useProfessionalAreas();

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        setProfessionalAreaId(data?.professionalAreaId ?? '');
        setCode(data?.code ?? '');
        setName(data?.name ?? '');
        setDescription(data?.description ?? '');
        fetchProfessionalAreas();
    }, [isOpen, data]);

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();

        if (!professionalAreaId) {
            toast.error('Debes seleccionar un área profesional');
            return;
        }

        if (!code.trim()) {
            toast.error('El código es obligatorio');
            return;
        }

        if (!name.trim()) {
            toast.error('El nombre es obligatorio');
            return;
        }

        setLoading(true);
        try {
            const service = new TrainingActionGroupService();
            const response = data?.id
                ? await (await service.edit({
                    id: data.id,
                    professionalAreaId,
                    code: code.trim(),
                    name: name.trim(),
                    description: description.trim() || null,
                })).getResponseData()
                : await (await service.create({
                    professionalAreaId,
                    code: code.trim(),
                    name: name.trim(),
                    description: description.trim() || null,
                })).getResponseData();

            if (response?.success) {
                toast.success(data?.id ? 'Grupo actualizado correctamente' : 'Grupo creado correctamente');
                onClose();
                refetch();
            } else {
                handleErrors(response);
            }
        } catch {
            toast.error('Error al guardar el grupo de acciones formativas');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose}>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
                <h3 className="text-lg font-semibold">
                    {data?.id ? 'Editar Grupo de Acciones Formativas' : 'Crear Grupo de Acciones Formativas'}
                </h3>

                <CustomSelect
                    id="professionalAreaId"
                    label="Área profesional"
                    options={getProfessionalAreasList()}
                    value={
                        professionalAreaId
                            ? getProfessionalAreasList().find((item: any) => item.value === professionalAreaId) || null
                            : null
                    }
                    onChange={(selected: any) => setProfessionalAreaId(selected?.value || '')}
                    isSearchable
                    isClearable
                />
                <Input
                    id="code"
                    label="Código"
                    value={code}
                    onChange={(event) => setCode(event.target.value)}
                />
                <Input
                    id="name"
                    label="Nombre"
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                />
                <Textarea
                    id="description"
                    label="Descripción"
                    value={description}
                    onChange={(event) => setDescription(event.target.value)}
                />

                <div className="flex gap-2 justify-end pt-4">
                    <button type="button" onClick={onClose} className="px-4 py-2 border rounded">
                        Cancelar
                    </button>
                    <button type="submit" disabled={loading} className="px-4 py-2 bg-primary text-white rounded">
                        {loading ? 'Guardando...' : 'Guardar'}
                    </button>
                </div>
            </form>
        </Modal>
    );
};

const TrainingActionGroupsList = () => {
    const { filters, updateFilters, updateFilterOrder, updatePage, updatePageSize } = useFilters();
    const { userCan } = usePrivilege();
    const { handleErrors } = useHandleErrors();
    const service = new TrainingActionGroupService();

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState<any>(null);

    const [data, loading, , refetch] = useFetch(useCallback(async () => {
        const response = await service.list(filters);
        return response.getResponseData();
    }, [filters]));

    const handleDelete = async (id: string) => {
        const response = await (await service.delete(id)).getResponseData();
        if (response?.success) {
            toast.success('Grupo eliminado correctamente');
            refetch();
        } else {
            handleErrors(response);
        }
    };

    const handleEdit = useCallback((row: any) => {
        setSelectedItem({
            id: row.id,
            code: row.code,
            name: row.name,
            description: row.description ?? '',
            professionalAreaId: row.professionalArea?.id ?? '',
        });
        setIsModalOpen(true);
    }, []);

    const handleCloseModal = useCallback(() => {
        setIsModalOpen(false);
        setSelectedItem(null);
    }, []);

    const orderedData = () => data?.data?.map((row: any) => ({
        id: row.id,
        code: row.code,
        name: row.name,
        professionalArea: row.professionalArea ?? null,
        description: row.description ?? '',
    })) ?? [];

    return (
        <div className="space-y-6">
            {data !== undefined && (
                <CustomTable
                    largeTable
                    id="training-action-groups-table"
                    columnOrder={columnOrder}
                    data={orderedData()}
                    isLoading={loading}
                    filter={false}
                    filters={filters}
                    updateFilters={updateFilters}
                    updateFilterOrder={updateFilterOrder}
                    defaultOrder={filters.filter_order || undefined}
                    overrideColumns={[
                        {
                            key: 'professionalArea',
                            columnNameKey: 'Área profesional',
                            render: (row: any) => {
                                const area = row.row.original.professionalArea;
                                if (!area) return 'N/A';
                                return `${area.code} - ${area.name}`;
                            },
                        },
                    ]}
                    paginationData={{
                        pageSize: filters.limit,
                        currentPage: filters.page,
                        pageCount: data?.lastPage ?? 1,
                        totalCount: data?.totalRegisters,
                        handlePagination: updatePage,
                        handlePerPage: updatePageSize,
                    }}
                    actions={[
                        {
                            label: 'Edit',
                            permissions: { group: 'products', permission: 'admin_products' },
                            render: (row: any) => (
                                <Tooltip size="sm" content="Editar" placement="top" color="invert">
                                    <ActionIcon
                                        as="span"
                                        size="sm"
                                        variant="outline"
                                        className="hover:!border-gray-900 hover:text-gray-700 cursor-pointer"
                                        onClick={() => handleEdit(row)}
                                    >
                                        <MdOutlineModeEditOutline className="h-4 w-4" size={20} />
                                    </ActionIcon>
                                </Tooltip>
                            ),
                        },
                        {
                            label: 'Delete',
                            permissions: { group: 'products', permission: 'admin_products' },
                            render: (row: any) => (
                                <Tooltip size="sm" content="Eliminar" placement="top" color="invert">
                                    <div>
                                        <DeletePopover
                                            title="Eliminar grupo de acciones formativas"
                                            description={`¿Estás seguro de que deseas eliminar "${row.name}"?`}
                                            onDelete={() => handleDelete(row.id)}
                                        />
                                    </div>
                                </Tooltip>
                            ),
                        },
                    ]}
                >
                    {userCan('admin_products', 'products') && (
                        <Button onClick={() => { setSelectedItem(null); setIsModalOpen(true); }}>
                            Crear grupo de acciones formativas
                        </Button>
                    )}
                </CustomTable>
            )}

            <TrainingActionGroupModalForm
                data={selectedItem}
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                refetch={refetch}
            />
        </div>
    );
};

export default TrainingActionGroupsList;
